import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Badge,
  ButtonGroup,
  Checkbox,
  useToast,
} from '@chakra-ui/react';
import { serviceApi } from '../services/api';

function ServiceList() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line
  }, [showInactive]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await serviceApi.getAll(!showInactive);
      setServices(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch services');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this service?')) {
      try {
        await serviceApi.delete(id);
        toast({
          title: 'Service deactivated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchServices();
      } catch (err) {
        toast({
          title: 'Failed to deactivate service',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(err);
      }
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="xl" color="brand.purple" thickness="4px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Heading size="lg">Services & Pricing</Heading>
        <Flex gap={4} align="center">
          <Checkbox
            isChecked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            colorScheme="purple"
          >
            Show Inactive
          </Checkbox>
          <Button as={RouterLink} to="/app/services/new" colorScheme="purple">
            Add New Service
          </Button>
        </Flex>
      </Flex>

      {services.length === 0 ? (
        <Text color="gray.500">No services found. Add your first service!</Text>
      ) : (
        <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="sm">
          <Table variant="simple">
            <Thead bg="brand.darkPurple">
              <Tr>
                <Th color="white">ID</Th>
                <Th color="white">Service Name</Th>
                <Th color="white">Category</Th>
                <Th color="white">Description</Th>
                <Th color="white">Price</Th>
                <Th color="white">Duration</Th>
                <Th color="white">Status</Th>
                <Th color="white">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {services.map((service) => (
                <Tr key={service.id} opacity={service.is_active ? 1 : 0.5} _hover={{ bg: 'brand.lightGray' }}>
                  <Td>{service.id}</Td>
                  <Td fontWeight="bold">{service.name}</Td>
                  <Td>
                    {service.category ? (
                      <Badge colorScheme="purple" variant="subtle">
                        {service.category}
                      </Badge>
                    ) : (
                      <Text color="gray.400" fontSize="sm">N/A</Text>
                    )}
                  </Td>
                  <Td>{service.description || 'N/A'}</Td>
                  <Td>${parseFloat(service.price).toFixed(2)}</Td>
                  <Td>{service.duration_minutes ? `${service.duration_minutes} min` : 'N/A'}</Td>
                  <Td>
                    <Badge colorScheme={service.is_active ? 'green' : 'red'}>
                      {service.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    <ButtonGroup size="sm" spacing={2}>
                      <Button
                        as={RouterLink}
                        to={`/app/services/edit/${service.id}`}
                        colorScheme="purple"
                        variant="outline"
                      >
                        Edit
                      </Button>
                      {service.is_active && (
                        <Button
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDelete(service.id)}
                        >
                          Deactivate
                        </Button>
                      )}
                    </ButtonGroup>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default ServiceList;
